import { Checkbox, FormControlLabel, Link, Stack, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import ImageComponent from 'src/components/common/Image';
import ModalComponent from 'src/components/Modal/ModalComponent';
import { useNotification } from 'src/hooks/useNotification';
import useRefetch from 'src/hooks/useRefetch';
import { useMutateChangePlan } from 'src/services/mutate';
import { QUERY_KEY_USAGE } from 'src/services/queries';
import { REFUND_POLICY } from 'src/utils/constants/config';

// Utilities
import { ICON_NO_DATA_COMPANY, ICON_UPGRADE_PLAN } from 'src/utils/constants/imagesUrl';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { renderPriceOrDefault } from 'src/utils/helpers/stringHelpers';

const PopupChangePlan = ({
  priceDifference,
  priceId,
  open,
  handleClose,
}: {
  priceDifference: number;
  priceId: string;
  open: boolean;
  handleClose: () => void;
}) => {
  const theme = useTheme();
  const { notification } = useNotification();
  const { mutateAsync, isPending } = useMutateChangePlan();
  const [agreeTermsCondition, setAgreeTermsCondition] = useState(false);
  const { refetchQuery } = useRefetch();
  const isUpgrade = priceDifference > 0;

  const handleChangePrice = async () => {
    const responseChangePlan = await mutateAsync({ priceId: priceId });

    notification(responseChangePlan);
    if (responseChangePlan?.status_code === SUCCESS_CODE.GET) {
      handleClose();
      // Wait 2s to update data from Stripe
      setTimeout(() => {
        refetchQuery([QUERY_KEY_USAGE]);
      }, 2000);
    }
  };

  return (
    <ModalComponent
      closeOnBackdropClick={false}
      padding={4}
      open={open}
      handleClose={() => {
        handleClose();
      }}
      showCloseIcon={true}
    >
      <Stack direction="column" spacing={2} width={504} alignItems="center">
        <ImageComponent imgSrc={isUpgrade ? ICON_UPGRADE_PLAN : ICON_NO_DATA_COMPANY} height={150} />
        <Typography textAlign="center" variant="h6">
          {isUpgrade ? 'Better Plan Experience' : 'Confirm Your Plan Change'}
        </Typography>
        {isUpgrade ? (
          <Typography textAlign="center" variant="body1">
            Switch to the new plan for just{' '}
            <Typography component="span" color="secondary.main">
              ${renderPriceOrDefault(Math.abs(priceDifference))} more
            </Typography>
            . Enjoy a smooth upgrade process with all your credits renewed at the new rate.
          </Typography>
        ) : (
          <Typography textAlign="center" variant="body1">
            By switching to the new plan all credits will be renewed. Please note that this action is final, and no
            refunds will be provided for unused credits.
          </Typography>
        )}
        <FormControlLabel
          id="agree_terms_condition"
          name="agree_terms_condition"
          onChange={(_, value) => {
            setAgreeTermsCondition(value);
          }}
          value={agreeTermsCondition}
          control={<Checkbox />}
          label={
            <Typography color={theme.palette.text.secondary} variant="body1">
              I agree to the{' '}
              <Link sx={{ margin: 0 }} target="_blank" href={REFUND_POLICY}>
                ADAMftd's Terms, Conditions and Refunds
              </Link>
            </Typography>
          }
        />
        <ButtonWithLoading
          variant="contained"
          color={isUpgrade ? 'primary' : 'error'}
          size="large"
          onClick={() => {
            handleChangePrice();
          }}
          fullWidth
          isLoading={isPending}
          disabled={!agreeTermsCondition || isPending}
        >
          {isUpgrade ? 'Check out' : 'YES, PLEASE CHANGE MY PLAN'}
        </ButtonWithLoading>
      </Stack>
    </ModalComponent>
  );
};

export default PopupChangePlan;
